import { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  formatWeight,
  muscleName,
  type Exercise,
  type LogSet,
  type Session,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { weightUnitLabels } from "../weightUnitLabels";
import { CatalogRecordsCard } from "./CatalogRecordsCard";
import { useDateLocale } from "../primitives/DateFormatContext";
import { PauseExplainer } from "../PauseExplainer";

// THE Catalog detail body — one tree for mobile's CatalogDetail screen and
// the web-rnw master-detail pane. Renders metadata + per-exercise records +
// description + aliases + tags + recent-session history for ONE exercise.
// The title/back chrome is the container's job (mobile = native header,
// web = pane heading / back row). History is fetched here (keyed by slug)
// via the injected apiFetch, so the web detail pane re-fetches on select.

const HISTORY_LIMIT = 50;
const HISTORY_VISIBLE = 10;

type HistoryEntry = { session: Session; sets: LogSet[] };
type HistoryState =
  | { kind: "loading" }
  | { kind: "ready"; rows: HistoryEntry[] }
  | { kind: "error" };

export type CatalogDetailViewProps = {
  theme: UiTheme;
  exercise: Exercise;
  unit: "kg" | "lb";
  apiFetch: ProgramApiFetch;
  formatDate: (iso: string, locale: string) => string;
};

export function CatalogDetailView({
  theme,
  exercise,
  unit,
  apiFetch,
  formatDate,
}: CatalogDetailViewProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = useDateLocale();
  const unitLabels = weightUnitLabels(t);
  const slug = exercise.slug;
  const styles = makeStyles(theme);
  const [history, setHistory] = useState<HistoryState>({ kind: "loading" });

  // Fetch the recent sessions window once per slug-change, filter to
  // entries that performed THIS exercise. Reuses the gsi2 query path so
  // the family-scale read stays cheap even at 50 sessions.
  useEffect(() => {
    let cancelled = false;
    setHistory({ kind: "loading" });
    void (async () => {
      try {
        const res = await apiFetch<{
          items: Array<{ session: Session; sets: LogSet[] }>;
        }>(`/api/sessions?include=sets&limit=${HISTORY_LIMIT}`);
        if (cancelled) return;
        const rows: HistoryEntry[] = [];
        for (const item of res.items) {
          const matches = item.sets.filter((s) => s.exerciseSlug === slug);
          if (matches.length === 0) continue;
          rows.push({ session: item.session, sets: matches });
          if (rows.length >= HISTORY_VISIBLE) break;
        }
        setHistory({ kind: "ready", rows });
      } catch {
        if (!cancelled) setHistory({ kind: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, apiFetch]);

  const description = useMemo(() => {
    if (!exercise.description) return null;
    const lng = i18n.language as keyof typeof exercise.description;
    return exercise.description[lng] ?? exercise.description.en ?? null;
  }, [exercise, i18n.language]);

  return (
    <ScrollView contentContainerStyle={styles.body}>
      <View style={styles.metaCard}>
        {exercise.primaryMuscle ? (
          <Meta
            label={t("mobile.catalog.metaPrimaryMuscle")}
            value={muscleName(exercise.primaryMuscle, coerceLocale(i18n.language))}
            theme={theme}
          />
        ) : null}
        {exercise.movementPattern ? (
          <Meta
            label={t("mobile.catalog.metaPattern")}
            value={t(`movementPattern.${exercise.movementPattern}`, {
              defaultValue: exercise.movementPattern,
            })}
            theme={theme}
          />
        ) : null}
        {exercise.equipment ? (
          <Meta
            label={t("mobile.catalog.metaEquipment")}
            value={t(`equipment.${exercise.equipment}`, {
              defaultValue: exercise.equipment,
            })}
            theme={theme}
          />
        ) : null}
        <Meta
          label={t("mobile.catalog.metaP1RM")}
          value={
            exercise.hasP1RM
              ? t("mobile.catalog.metaP1RMYes")
              : t("mobile.catalog.metaP1RMNo")
          }
          theme={theme}
        />
        {exercise.defaultBarKg !== undefined ? (
          <Meta
            label={t("mobile.catalog.metaDefaultBar")}
            value={formatWeight(
              exercise.defaultBarKg,
              unit,
              i18n.language,
              unitLabels,
            )}
            theme={theme}
          />
        ) : null}
        {exercise.barLoading === "single-side" ? (
          <Meta
            label={t("mobile.catalog.metaBarLoading")}
            value={t("mobile.catalog.metaBarLoadingSingleSide")}
            theme={theme}
          />
        ) : null}
      </View>

      <CatalogRecordsCard
        theme={theme}
        slug={slug}
        unit={unit}
        locale={i18n.language}
        apiFetch={apiFetch}
        formatDate={formatDate}
      />

      {description ? (
        <View style={styles.descCard}>
          <Text style={styles.descText}>{description}</Text>
        </View>
      ) : null}

      {/* HI-8: what a pause's phase means, wherever a program can ask for
          one (an exercise logged by reps). Static words: nothing about a
          program is copied onto the exercise. */}
      {!exercise.isTimeBased && exercise.movementPattern !== "cardio" ? (
        <View style={styles.tagCard}>
          <Text style={styles.cardLabel} accessibilityRole="header">
            {t("program.pause.explainerTitle", { defaultValue: "Pause inside a rep" })}
          </Text>
          <PauseExplainer theme={theme} />
        </View>
      ) : null}

      {(exercise.aliases ?? []).length > 0 ? (
        <View style={styles.tagCard}>
          <Text style={styles.cardLabel}>
            {t("mobile.catalog.aliasesLabel")}
          </Text>
          <View style={styles.chipRow}>
            {(exercise.aliases ?? []).map((a) => (
              <View key={a} style={styles.chip}>
                <Text style={styles.chipText}>{a}</Text>
              </View>
            ))}
          </View>
        </View>
      ) : null}

      {(exercise.tags ?? []).length > 0 ? (
        <View style={styles.tagCard}>
          <Text style={styles.cardLabel}>{t("mobile.catalog.tagsLabel")}</Text>
          <View style={styles.chipRow}>
            {(exercise.tags ?? []).map((tag) => (
              <View key={tag} style={styles.chip}>
                <Text style={styles.chipText}>{tag}</Text>
              </View>
            ))}
          </View>
        </View>
      ) : null}

      <View style={styles.historyCard}>
        <Text style={styles.cardLabel}>
          {t("mobile.catalog.historyLabel", { defaultValue: "Recent sessions" })}
        </Text>
        {history.kind === "loading" ? (
          <ActivityIndicator color={theme.palette.ink3} />
        ) : history.kind === "error" ? (
          <Text style={styles.historyEmpty}>
            {t("mobile.catalog.historyError", {
              defaultValue: "Could not load history.",
            })}
          </Text>
        ) : history.rows.length === 0 ? (
          <Text style={styles.historyEmpty}>
            {t("mobile.catalog.historyEmpty", {
              defaultValue: "No past sessions for this exercise.",
            })}
          </Text>
        ) : (
          history.rows.map((row) => (
            <View key={row.session.id} style={styles.historyRow}>
              <Text style={styles.historyDate}>
                {formatDate(row.session.startedAt, dateLocale)}
              </Text>
              <View style={styles.historySets}>
                {row.sets.map((s, idx) => (
                  <Text
                    key={`${row.session.id}-${idx}`}
                    style={styles.historySet}
                    numberOfLines={1}
                  >
                    {s.kind === "weighted" && s.weight !== undefined
                      ? `${s.reps ?? "—"} × ${formatWeight(
                          s.weight,
                          unit,
                          i18n.language,
                          unitLabels,
                        )}`
                      : s.kind === "bodyweight"
                        ? t("mobile.catalog.historyBodyweight", {
                            defaultValue: "{{reps}} reps BW",
                            reps: s.reps ?? 0,
                          })
                        : `${s.reps ?? "—"} ×`}
                  </Text>
                ))}
              </View>
            </View>
          ))
        )}
      </View>
    </ScrollView>
  );
}

function Meta({
  label,
  value,
  theme,
}: {
  label: string;
  value: string;
  theme: UiTheme;
}) {
  const styles = makeStyles(theme);
  return (
    <View style={styles.metaRow}>
      <Text style={styles.metaLabel}>{label}</Text>
      <Text style={styles.metaValue}>{value}</Text>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    body: {
      paddingHorizontal: s.s5,
      paddingBottom: s.s6,
      gap: s.s4,
    },
    metaCard: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s2,
    },
    metaRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: s.s3,
    },
    metaLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    metaValue: {
      color: p.ink,
      fontSize: t.sizes.sm,
      flexShrink: 1,
      textAlign: "right",
    },
    descCard: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      padding: s.s4,
    },
    descText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      lineHeight: 22,
    },
    tagCard: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s2,
    },
    cardLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    chipRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: s.s2,
    },
    chip: {
      backgroundColor: p.surface2,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: 999,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
    },
    chipText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
    },
    historyCard: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s3,
    },
    historyEmpty: {
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    historyRow: {
      gap: s.s2,
      paddingTop: s.s2,
      borderTopColor: p.border,
      borderTopWidth: 1,
    },
    historyDate: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    historySets: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: s.s2,
    },
    historySet: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "600",
      fontVariant: ["tabular-nums"],
      backgroundColor: p.surface2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s1,
      borderRadius: r.r2,
    },
  });
}
